import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { isAxiosApiError } from "@base/lib/utils";
import { Button, Dialog } from "@buildblockinc/buildblock-design-system";
import { toast } from "sonner";
import { useCreateImageZipJobMutation } from "../model/queries";
import { imageZipApiErrorCodeSchema } from "../model/schemas";

interface DownloadImagesConfirmModalProps {
  isOpen: boolean;
  close: () => void;
  imageCount: number;
  projectId: string;
  dailyLogId: string;
  onJobCreated: (jobId: string) => void;
}

export function DownloadImagesConfirmModal({
  isOpen,
  close,
  imageCount,
  projectId,
  dailyLogId,
  onJobCreated,
}: DownloadImagesConfirmModalProps) {
  const createJobMutation = useCreateImageZipJobMutation();

  const handleConfirm = async () => {
    const baseProperties = { project_id: projectId, log_id: dailyLogId, image_count: imageCount };
    EventLogger.click(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_START, baseProperties);

    try {
      const result = await createJobMutation.mutateAsync({ projectId, dailyLogId });
      onJobCreated(result.job_id);
      close();
    } catch (error) {
      const errorCode = isAxiosApiError(error) ? error.response.data.code : undefined;
      EventLogger.result(CLIENT_EVENT.IMAGE_ZIP_DOWNLOAD_FAIL, {
        ...baseProperties,
        reason: "job_create_failed",
        error_code: errorCode ?? null,
      });

      if (isAxiosApiError(error)) {
        const code = error.response.data.code;
        const parsed = imageZipApiErrorCodeSchema.safeParse(code);
        if (parsed.success && parsed.data === "NO_IMAGES") {
          toast.error("No photos to download.");
          close();
          return;
        }
      }
      toast.error("Failed to start download. Please try again.");
      close();
    }
  };

  return (
    <Dialog open={isOpen} onClose={close}>
      <Dialog.Content>
        <div className="flex flex-col gap-6 px-4 pt-8 pb-4">
          <div className="flex flex-col gap-y-6 text-center">
            <h2 className="text-title6 text-gray-1000">Download All Photos?</h2>
            <div>
              <p className="text-gray-900 text-body1">{imageCount} photos will be saved to your device as ZIP file.</p>
              <p className="text-gray-500 text-body3">(Wi-Fi recommended for large downloads)</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              color="secondary"
              className="flex-1"
              onClick={close}
              disabled={createJobMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="fill"
              color="info"
              className="flex-1"
              onClick={handleConfirm}
              isLoading={createJobMutation.isPending}
            >
              Download
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
